import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  SecurityContext,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  OnDestroy,
  ViewChild
} from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import {
  GridOptions,
  ColDef,
  ColumnResizedEvent,
  GridReadyEvent,
  GridApi,
  Module,
  ClientSideRowModelModule,
  RowSelectionOptions
} from 'ag-grid-community'
import moment from 'moment'
import { ODataFilterService, ODataFunction, ODataService } from 'common-services'
import { Subscription } from 'rxjs'
import { IModelBase } from '../modelBase'
import { Option } from '../app-select/option'
import { LookupFilterComponent } from './filters/lookup-filter/lookup-filter.component'
import { AgGridAngular } from 'ag-grid-angular'

export enum GridSize {
  xsmall,
  small,
  normal,
  large,
  xlarge
}

let gridId = 0
const ROW_HEIGHT = 29

interface OrderModel {
  colId: string
  sort?: string
}

@Component({
  selector: 'app-grid-wrapper',
  templateUrl: './grid-wrapper.component.html',
  standalone: false
})
export class GridWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  modules: Module[] = [ClientSideRowModelModule]
  private static sanitizer: DomSanitizer
  private _gridSize: GridSize = GridSize.normal
  busyOptions: { busy: Subscription } = null
  private isDisposed = false

  @ViewChild('agGrid') agGrid!: AgGridAngular
  public api: GridApi<any>

  @Input() data = null
  @Input() selectRowOnCellClick = false

  @Output() clearFilterClicked = new EventEmitter()
  @Output() clearSortClicked = new EventEmitter()
  @Output() getDataCalled = new EventEmitter()
  @Output() getDataCompleted = new EventEmitter()
  @Output() cellValueChanged = new EventEmitter<any>()
  @Output() cellDoubleClicked = new EventEmitter<any>()
  @Output() gridReadyEvent = new EventEmitter<any>()
  @Output() columnResizedEvent = new EventEmitter<any>()

  @Input() identifier = `app-grid-wrapper-${gridId++}`
  @Input() hasPagination = true
  @Input() pageSizeOptions: number[] = [10, 20, 50, 100, 200]
  @Input() supressSizeToFit = false
  @Input() alwaysShowOptionalColumns = false
  @Input() hideLoadingMask = false
  @Input() autoHeight = true
  @Input() expandVerticalColumns: string[] = []
  @Input() frameworkComponents: any = null

  @Input()
  set gridSize(value: GridSize) {
    this._gridSize = value
    switch (value) {
      case GridSize.xsmall:
        this.gridStyle = {
          width: '100%',
          height: '200px'
        }
        this.pageSize = 5
        break
      case GridSize.small:
        this.gridStyle = {
          width: '100%',
          height: '350px'
        }
        this.pageSize = 10
        break
      case GridSize.large:
        this.gridStyle = {
          width: '100%',
          height: '900px'
        }
        this.pageSize = 50
        break
      case GridSize.xlarge:
        this.gridStyle = {
          width: '100%',
          height: '1175px'
        }
        this.pageSize = 100
        break
      default:
        this.gridStyle = {
          width: '100%',
          height: '615px'
        }
        this.pageSize = 20
        break
    }
  }
  @Input() persistenceKey: string = null
  @Input() showGridTools = true

  get gridSize(): GridSize {
    return this._gridSize
  }
  public gridOptions: GridOptions
  public optionalColumns: string[] = []
  private _hasHiddenColumns = false
  public get hasHiddenColumns(): boolean {
    return this._hasHiddenColumns
  }
  public odata: ODataService<IModelBase>

  public defaultFilter = ''
  public defaultSelect: string | string[] = ''
  public defaultExpand = ''
  public defaultOrder: OrderModel[] = []

  private _filter = ''
  private _order = ''
  private _page = 1
  private _pageSize = 20
  private _firstRowIndex = 1
  private _lastRowIndex = 20
  private _totalCount = 0
  private _initialized = false
  private _totalHeight = 0
  private _displayedRows = 0
  private _canvas = null
  private _context = null
  private _function: ODataFunction = null

  gridStyle = null

  public get odataFunction(): ODataFunction {
    return this._function
  }
  public set odataFunction(value: ODataFunction) {
    this._function = value
    this._page = 1
    this.getData()
  }

  // This is the odata filter string
  public get filter(): string {
    return this._filter || this.defaultFilter
  }
  public set filter(value: string) {
    this._filter = value
    this._page = 1
    this.getData()
  }

  public get order(): string {
    return this._order || this.getOrder(this.defaultOrder)
  }
  public set order(value: string) {
    this._order = value
    this._page = 1
    this.getData()
  }

  public get pageSize(): number {
    return this._pageSize
  }
  public set pageSize(value: number) {
    this._pageSize = +value
    this._page = 1
    this.getData()
  }

  public get page(): number {
    return this._page
  }
  public set page(value: number) {
    const val = +value
    if (val < 1 || (this.totalPages > 0 && val > this.totalPages)) {
      return
    }
    this._page = val
    this.getData()
  }

  public get totalCount(): number {
    return this._totalCount
  }

  public get totalPages(): number {
    return Math.ceil(this._totalCount / this._pageSize) || 0
  }

  public get skipRows(): number {
    return (this._page - 1) * this._pageSize
  }

  public get firstRowIndex(): number {
    return this.totalCount ? this._firstRowIndex : 0
  }
  public get lastRowIndex(): number {
    return this.totalCount ? this._lastRowIndex : 0
  }

  public static BooleanRenderer(trueValue = 'Yes', falseValue = 'No'): any {
    return (params: any) => (params.value ? trueValue : falseValue)
  }

  public static PhoneNumberRenderer(): any {
    return (params: any) => {
      let value = params.value || ''
      value = GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, value)
      value =
        value !== ''
          ? '(' + value.substr(0, 3) + ') ' + value.substr(3, 3) + '-' + value.substr(6)
          : value
      return `${value}`
    }
  }

  public static HighPriorityRenderer(): any {
    return (params: any) => {
      const value = params.value || ''
      return `<span title="${value}"><i style="${
        !params.data.IsHighPriority ? 'display: none !important;' : ''
      }" class="fas fa-exclamation red-text"></i></span>`
    }
  }

  public static ExpectedAdmissionRenderer(): any {
    return (params: any) => {
      const value = params.value ? 'Expected Admission' : ''
      return `<span title="${value}"><i style="${
        !params.data.ExpectedAdmission ? 'display: none !important;' : ''
      }" class="fas fa-exclamation red-text"></i></span>`
    }
  }

  public static HasAssessmentRenderer(): any {
    return (params: any) => {
      const hasAssessment = params.data.AssessmentId ? true : false
      return `<span title="${hasAssessment}"><i style="${
        hasAssessment ? '' : 'display: none !important;'
      }" class="fas fa-file-medical-alt"></i></span>`
    }
  }

  public static LookupFieldRenderer(lookupList: Option[]): any {
    return (params: any) =>
      GridWrapperComponent.sanitizer.sanitize(
        SecurityContext.HTML,
        (lookupList.find(x => x.id === params.value) || ({} as Option)).text || ''
      )
  }

  public static MultiLookupFieldRenderer(lookupList: Option[]): any {
    return (params: any) => {
      let text = ''
      const val = params?.value ?? ''

      if (typeof val === 'string') {
        const value = val.split(',') || []
        text =
          lookupList
            .filter(x => value.includes(`${x.id}`))
            .map(x => x.text)
            .join(', ') || ''
      } else if (typeof val === 'number') {
        text =
          lookupList
            .filter(x => val === x.id)
            .map(x => x.text)
            .join(', ') || ''
      } else {
        text =
          lookupList
            .filter(x => val.includes(x.id))
            .map(x => x.text)
            .join(', ') || ''
      }
      return GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, text)
    }
  }

  public static ContainsLookupFieldRenderer(lookupList: Option[]): any {
    return (params: any) => {
      let text = ''
      const val = params?.value ?? ''

      if (typeof val === 'string') {
        const value = val.split(',') || []
        text =
          lookupList
            .filter(x => value.includes(`${x.id}`))
            .map(x => x.text)
            .join(', ') || ''
      } else {
        text =
          lookupList
            .filter(x => val.includes(x.id))
            .map(x => x.text)
            .join(', ') || ''
      }
      return GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, text)
    }
  }

  public static TooltipRenderer(): any {
    return (params: any) => {
      let value = (params && params.value) || ''
      value = GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, value)
      return `<span title="${value}">${value}</span>`
    }
  }

  public static ZipcodeRenderer(): any {
    return (params: any) => {
      let value = params.value || ''
      if (value.length === 9 && !value.includes('-')) {
        value = `${value.substring(0, 5)}-${value.substring(5)}`
      }
      value = GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, value)
      return `<span title="${value}">${value}</span>`
    }
  }

  public static CurrencyRenderer(): any {
    return (params: any) => {
      let value = params.value ? (+params.value || 0).toFixed(2) : '-&nbsp;&nbsp;&nbsp;'
      value = GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, value)
      return `<span class="pull-left">$</span><span class="pull-right">${value}</span>`
    }
  }

  public static PercentRenderer(): any {
    return (params: any) => {
      let value = params.value ? (+params.value || 0).toFixed(2) : '-&nbsp;&nbsp;&nbsp;'
      value = GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, value)
      return `<span class="pull-left">${value}</span><span class="pull-right">%</span>`
    }
  }

  public static ListRenderer(): any {
    return (params: any) => {
      return params.value ? params.value.join(',') : ''
    }
  }

  public static DateRenderer(format = 'L', dateOnly = false): any {
    return (params: any) => {
      const parseFormat = dateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ssZ'
      const dt = moment(params.value, parseFormat)
      return dt.isValid() ? dt.format(format) : ''
    }
  }

  public static DateWithoutTimeRenderer(): any {
    return (params: any) => {
      return params && params.value ? moment.utc(params.value).format('MM/DD/YYYY') : null
    }
  }

  public static TaxIdRenderer(): any {
    return (params: any) => {
      let value = ''
      if (params.value != null && params.value !== '0') {
        const lenStr = params.value.length
        const firstTwoStr = params.value.substring(0, 2)
        const restStr = params.value.substring(2, lenStr)
        value = `${firstTwoStr}-${restStr}`
      }
      value = GridWrapperComponent.sanitizer.sanitize(SecurityContext.HTML, value)
      return `<span class="pull-right">${value}</span>`
    }
  }

  public static FnRenderer(fnLink: (value: any, data: any) => string) {
    return (params: any) =>
      GridWrapperComponent.sanitizer.sanitize(
        SecurityContext.HTML,
        fnLink(params.value, params.data)
      )
  }

  public static TimeRenderer(): any {
    return (params: any) => {
      if (!params.value) {
        return ''
      }

      const dt = new Date(params.value)
      let h = dt.getHours()
      const m = dt.getMinutes()
      const am = h < 12 ? 'AM' : 'PM'

      h = h % 12
      const hs = (h < 10 ? '0' : '') + h
      const hm = (m < 10 ? '0' : '') + m

      return `${hs}:${hm} ${am}`
    }
  }

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private odataFilterService: ODataFilterService
  ) {
    GridWrapperComponent.sanitizer = GridWrapperComponent.sanitizer || this.sanitizer
    this.gridOptions = this.getDefaultGridOptions()
  }

  ngOnInit() {
    this._canvas = document.createElement('canvas')
    this._context = this._canvas.getContext('2d')
    this._context.font = '400 14px "Fira Sans","Helvetica Neue"'
    this.gridOptions.onCellClicked = event => {
      if (this.selectRowOnCellClick) {
        event.node.setSelected(true)
      }
    }
  }

  ngAfterViewInit() {
    this.api = this.agGrid.api
    this._initialized = true
    this.getInitialData()
    this.api.sizeColumnsToFit()
  }

  ngOnDestroy() {
    this.isDisposed = true
  }

  public clearFilter(): void {
    if (this.api) {
      this.api.setFilterModel({})
    }
    this.clearFilterClicked.emit()
  }

  public clearSort(): void {
    if (this.api) {
      this.api.resetColumnState()
    }
    this.clearSortClicked.emit()
  }

  public fitColumns(): void {
    if (this.api) {
      this.api.sizeColumnsToFit()
    }
    this.clearSortClicked.emit()
  }

  public autoSizeColumns(): void {
    this.api.autoSizeAllColumns()
    this.clearSortClicked.emit()
  }

  public onModelUpdated(): void {
    if (!this.supressSizeToFit) {
      this.api.autoSizeAllColumns()
    }
  }

  public gotoFirstPage(): void {
    if (this.page !== 1) {
      this.page = 1
    }
  }

  public gotoLastPage(): void {
    if (this.page < this.totalPages) {
      this.page = this.totalPages
    }
  }

  public gotoPreviousPage(): void {
    if (this.page > 1) {
      this.page--
    }
  }

  public gotoNextPage(): void {
    if (this.page < this.totalPages) {
      this.page++
    }
  }

  protected getInitialData(): void {
    this._order = ''
    this._page = 1

    this.getData()
  }

  private safeParseJSON(input, responseIfError = '') {
    try {
      return JSON.parse(input)
    } catch (e) {
      return responseIfError
    }
  }

  private loadSavedFilters() {
    if (this.persistenceKey) {
      try {
        const odataFilter = localStorage.getItem(this.persistenceKey + '_odataFilter')
        const filters = this.safeParseJSON(localStorage.getItem(this.persistenceKey + '_filters'))

        this._filter = odataFilter || ''
        if (this.api) {
          this.api.setFilterModel(filters)
        }
      } catch (e) {
        console.warn('Error initializing saved filters for ' + this.persistenceKey)
        console.warn(e)
      }
    } else {
      this._filter = ''
    }
  }

  protected setupGridEvents(): void {
    this.api.addEventListener('filterChanged', () => {
      const filters = this.api.getFilterModel()
      this.filter = this.getFilter(filters)

      if (this.persistenceKey) {
        localStorage.setItem(this.persistenceKey + '_filters', JSON.stringify(filters))
        localStorage.setItem(this.persistenceKey + '_odataFilter', this.filter)
      }
    })

    this.api.addEventListener('sortChanged', () => {
      const columns = this.api.getColumnState()
      this.order = this.getOrder(columns.filter(c => c.sort))
    })

    this.loadSavedFilters()
  }

  public getData(): void {
    const self = this
    if (!this.expandVerticalColumns) {
      this.expandVerticalColumns = []
    }
    this._totalHeight = 0
    if (!this._initialized || !this.odata) {
      return
    }

    if (this.api) {
      self.api.setGridOption('loading', false)
    }
    this.gridOptions.overlayLoadingTemplate = undefined

    if (this.busyOptions) {
      this.busyOptions.busy.unsubscribe()
    }

    const busy = this.odata
      .query(this.odataFunction)
      .top(this.pageSize)
      .skip(this.skipRows)
      .filter(this.filter)
      .select(this.defaultSelect)
      .expand(this.defaultExpand)
      .orderBy(this.order)
      .exec()
      .subscribe({
        next: data => {
          // Default to data length if rowCount is undefined due to stored procedure results call
          self._totalCount = data.rowCount || data.data.length
          self._firstRowIndex = (self.page - 1) * self.pageSize + 1
          self._lastRowIndex = Math.min(self._totalCount, self.page * self.pageSize)
          self._displayedRows = data.data.length
          self._totalHeight = ROW_HEIGHT * (self._displayedRows + 1)
          if (self.autoHeight && self.api) {
            self.api.updateGridOptions({ domLayout: 'autoHeight' })
          } else if (!self.gridStyle) {
            self.gridStyle = {
              width: '100%',
              height: `${self._totalHeight}px`
            }
          }
          self.data = data.data
          self.busyOptions = null
          self.getDataCompleted.emit()
          if (!self.isDisposed) {
            self.cdr.detectChanges()
          }
        },
        error: e => {
          self._totalCount = 0
          self._firstRowIndex = 0
          self._lastRowIndex = 0
          self._displayedRows = 0
          self.data = null
          self.busyOptions = null

          if (!self.isDisposed) {
            self.cdr.detectChanges()
          }

          self.gridOptions.overlayLoadingTemplate =
            '<span style="color: red;">An error occurred loading the data.</span>'
          self.api.setGridOption('loading', true)
        }
      })

    if (!this.hideLoadingMask) {
      this.busyOptions = { busy }
      if (!this.isDisposed) {
        this.cdr.detectChanges()
      }
    }
    this.getDataCalled.emit()
  }

  public showOptionalColumns(): void {
    this.alwaysShowOptionalColumns = true

    const allColumns = this.api.getColumns()
    const columnsToShow = []
    for (const column of allColumns) {
      const colId = column.getColId()

      if (this.optionalColumns.indexOf(colId) > -1) {
        columnsToShow.push(colId)
      }
    }
    this._hasHiddenColumns = false
    this.api.setColumnsVisible(columnsToShow, true)

    // fill out any available space to ensure there are no gaps
    if (!this.supressSizeToFit && this.api) {
      this.api.sizeColumnsToFit()
    }
  }

  public hideOptionalColumns() {
    this.alwaysShowOptionalColumns = false
    this.adjustOptionalColumns()
  }

  protected getDefaultGridOptions(): GridOptions {
    const options = {} as GridOptions

    options.headerHeight = 30
    options.rowHeight = ROW_HEIGHT
    options.rowModelType = 'clientSide'
    if (this.hasPagination) {
      options.paginationPageSize = this.pageSize
    }
    options.defaultColDef = { resizable: true, sortable: true, cellDataType: false }
    options.onColumnResized = this.adjustOptionalColumns.bind(this)
    options.onGridReady = this.onGridReady.bind(this)
    options.onColumnResized = this.onColumnResized.bind(this)
    options.theme = 'legacy'

    return options
  }

  protected adjustOptionalColumns(): void {
    if (!this.optionalColumns.length || this.alwaysShowOptionalColumns || !this.supressSizeToFit) {
      return
    }
    const elem = document.getElementById(this.identifier)
    if (!elem) {
      return
    }
    const gridWidth = elem.offsetWidth

    // keep track of which columns to hide/show
    const columnsToHide = []
    const columnsToShow = []

    // iterate over all columns (visible or not) and work out
    // now many columns can fit (based on their minWidth)
    let totalColsWidth = 0
    const allColumns = this.api.getColumns()

    for (const column of allColumns) {
      if (column.getColDef().hide) {
        continue
      }

      const colId = column.getColId()
      totalColsWidth += Math.max(column.getActualWidth(), column.getMinWidth())

      if (this.optionalColumns.indexOf(colId) === -1) {
        continue
      }

      if (totalColsWidth > gridWidth) {
        columnsToHide.push(colId)
      } else {
        columnsToShow.push(colId)
      }
    }

    this._hasHiddenColumns = !!columnsToHide.length
    // show/hide columns based on current grid width
    this.api.setColumnsVisible(columnsToShow, true)
    this.api.setColumnsVisible(columnsToHide, false)

    // fill out any available space to ensure there are no gaps
    if (!this.supressSizeToFit && this.api) {
      this.api.sizeColumnsToFit()
    }
  }

  protected getOrder(orderModel: OrderModel[]): string {
    return orderModel
      .map(order => `${order.colId.replace('.', '/')} ${order.sort}`, [] as string[])
      .join()
  }

  protected getFilter(filterModel: any): string {
    const filters: string[] = this.defaultFilter ? [this.defaultFilter] : []

    for (const field in filterModel) {
      if (filterModel.hasOwnProperty(field)) {
        if (this.isMultiConditionFilter(filterModel[field])) {
          const fieldFilters: string[] = []
          filterModel[field].conditions.forEach((condition: any) => {
            fieldFilters.push(
              this.getODataFilter(
                {
                  [field]: condition
                },
                field
              )
            )
          })
          if (fieldFilters.length) {
            filters.push(fieldFilters.join(` ${filterModel[field].operator.toLowerCase()} `))
          }
        } else {
          const filter = this.getODataFilter(filterModel, field)

          if (filter) {
            filters.push(filter)
          }
        }
      }
    }

    return filters.join(' and ')
  }

  private getODataFilter(filterModel: any, field: string): string {
    const op: string = filterModel[field].type.toLowerCase()
    const filterType: string = this.getColumnFilterType(field)
    let val: string | string[]
    if (filterType === 'agDateColumnFilter') {
      const from = moment(this.getFilterValue(filterModel[field].dateFrom))
      const to = moment(this.getFilterValue(filterModel[field].dateTo))
      val = [`${from.format('YYYY-MM-DD')}`, `${to.format('YYYY-MM-DD')}`]
    } else if (filterType === 'agNumberColumnFilter') {
      val = this.getFilterValue(filterModel[field].filter.toString())
    } else {
      val = this.getFilterValue(filterModel[field].filter)
    }

    return this.odataFilterService.translateFilterToOData(field, op, val, filterType)
  }

  private isMultiConditionFilter(filterObject: any) {
    return filterObject.hasOwnProperty('conditions')
  }

  protected getFilterValue(value: string): string {
    value = value || ''

    return value.replace ? value.replace("'", "''") : value
  }

  protected getColumnFilterType(field: string): string {
    const match: ColDef = this.gridOptions.columnDefs.find((col: ColDef) => col.field === field)
    return typeof match.filter === 'string' ? (match.filter as string) : ''
  }

  onCellValueChanged(e) {
    this.cellValueChanged.emit(e)
  }

  onCellDoubleClicked(e) {
    this.cellDoubleClicked.emit(e)
  }

  onGridReady(e: GridReadyEvent) {
    if (this.api) {
      this.api.resetRowHeights()
    }
    this.gridReadyEvent.emit(e)
    this.setupGridEvents()
  }

  onColumnResized(e: ColumnResizedEvent) {
    this.columnResizedEvent.emit(e)
  }

  public refreshView() {
    if (this.api) {
      this.api.redrawRows()
    }
    this.cdr.detectChanges()
  }

  public setLookupFilterOptions(key: string, options: Option[]) {
    const filterPromise = this.api.getColumnFilterInstance<LookupFilterComponent>(key)
    filterPromise.then(filter => filter?.setItems(options))
  }
}

export const SingleRowSelection: RowSelectionOptions<any, any> = {
  mode: 'singleRow',
  checkboxes: false,
  enableClickSelection: true
}

export const MultiRowSelection: RowSelectionOptions<any, any> = {
  mode: 'multiRow',
  checkboxes: false,
  headerCheckbox: false,
  enableClickSelection: true
}
